import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  ChevronDown, Info, ShieldCheck, AlertCircle,
} from 'lucide-react';
import { toast } from 'react-toastify';
import {
  INDIA_CONSENT_ITEMS,
  getIndiaConsentsForSurface,
  buildInitialConsentState,
  areRequiredConsentsGiven,
  type IndiaConsentItem,
  type ConsentSurface,
} from 'src/config/consent/indiaConsentRegistry';
import { DP_CONTACTS, RETENTION, CLINICAL_GOVERNANCE } from 'src/config/consent/indiaConsentConfig';
import { IconTile } from 'src/components/Stepper/ui';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  CONSENT FORM
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *  Layered, per the ICO's just-in-time model and DPDP s.6(1):
 *
 *    Layer 0  One safety line — emergencies are the only thing urgent enough
 *             to interrupt before the patient has read anything.
 *    Layer 1  The decisions, as rows in one panel with a running count.
 *    Layer 2  Per-item detail on demand (Rule 3's itemised notice).
 *    Layer 3  Standing information, collapsed and one click away.
 *
 *  Optional consents are asked where they mean something (upload, video call,
 *  settings), not here. See the registry's surface notes.
 *
 *  Notice language is English only — a product decision (Aug 2026).
 */

const STRINGS = {
  requiredNotAccepted: 'Please accept the required items to continue.',
  emergencyWarning:
    'This service is not for emergencies. If you have chest pain, difficulty breathing, severe bleeding, sudden weakness or any other emergency, call 112 or go to your nearest hospital immediately.',
  notPrescriptive:
    'A second opinion is advice about your existing diagnosis and treatment. It does not replace your treating doctor and it is not a prescription.',
  pcpndt:
    'We do not perform, offer or disclose determination of the sex of a foetus. Doing so is a criminal offence under the Pre-Conception and Pre-Natal Diagnostic Techniques (Prohibition of Sex Selection) Act, 1994. Do not request it.',
} as const;

/** Icon + accent per consent, so a row is recognisable before it is read. */

interface Props {
  surface?: ConsentSurface;
  initialState?: Record<string, boolean>;
  onStatusChange?: (ready: boolean) => void;
  onChange?: (state: Record<string, boolean>) => void;
  triggerSubmit?: (fn: () => void) => void;
  onComplete?: (state: Record<string, boolean>) => void;
  /** Hide the standing legal notices (settings screen). */
  compact?: boolean;
  /**
   * The service just chosen, e.g. "Cardiology". DPDP s.6(1) ties consent to a
   * *specified* purpose, and naming it is the difference between "we process
   * your health data" and a purpose a person can actually evaluate.
   */
  contextLabel?: string;
  /** The stepper shell states the step name already. */
  hideHeading?: boolean;
}

/* ── Collapsible disclosure — layer 3 ─────────────────────────────────────── */

/**
 * Layer 3 — standing notices. Same row language as the consent list above
 * (hairline divider, quiet chevron, no icon tile) so the step reads as one
 * document rather than a stack of unrelated cards.
 */
const Disclosure: React.FC<{
  title: string;
  children: React.ReactNode;
  first?: boolean;
}> = ({ title, children, first = false }) => {
  const [open, setOpen] = useState(false);
  const panelId = `disclosure-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
  return (
    <div className={first ? '' : 'border-t border-[#e8eef5] dark:border-white/[0.07]'}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex w-full items-center gap-3 px-5 py-[15px] text-left outline-none transition-colors hover:bg-[#fafcfe] focus-visible:bg-[#f4faff] sm:px-6 dark:hover:bg-white/[0.02] dark:focus-visible:bg-white/[0.03]"
      >
        <Info size={15} className="flex-shrink-0 text-[#8398ab] dark:text-slate-400" aria-hidden="true" />
        <span className="flex-1 text-[13.5px] font-semibold text-[#28455f] dark:text-slate-200">
          {title}
        </span>
        <ChevronDown
          size={15}
          className={['flex-shrink-0 text-[#8398ab] transition-transform duration-200 dark:text-slate-400',
            open ? 'rotate-180' : ''].join(' ')}
        />
      </button>
      {open && (
        <div id={panelId} className="border-t border-[#e8eef5] bg-[#fbfdfe] px-5 py-4 sm:px-6 dark:border-white/[0.07] dark:bg-black/20">
          {children}
        </div>
      )}
    </div>
  );
};

/* ── One consent decision ─────────────────────────────────────────────────── */

const ConsentRow: React.FC<{
  item: IndiaConsentItem;
  checked: boolean;
  onToggle: () => void;
  index: number;
}> = ({ item, checked, onToggle, index }) => {
  const [expanded, setExpanded] = useState(false);
  const checkboxId = `consent-${item.id}`;
  const detailsId = `consent-details-${item.id}`;

  const sections = [
    { label: 'What we collect', values: item.dataItems, list: true },
    { label: 'Why we use it', values: [item.purpose], list: false },
    { label: 'Who sees it', values: item.recipients, list: true },
    { label: 'Legal basis', values: [item.legalBasis], list: false },
    { label: 'If you say no', values: [item.consequenceOfDeclining], list: false },
    { label: 'How long we keep it', values: [item.retention], list: false },
  ];

  return (
    <div
      className={[
        'relative transition-colors duration-150',
        index > 0 ? 'border-t border-[#e8eef5] dark:border-white/[0.07]' : '',
        checked
          ? 'bg-[#f4faff] dark:bg-sky-400/[0.045]'
          : 'bg-transparent hover:bg-[#fafcfe] dark:hover:bg-white/[0.02]',
      ].join(' ')}
    >
      {/* Selected marker — a quiet rail, not a border colour change */}
      <span
        aria-hidden="true"
        className={[
          'absolute inset-y-0 left-0 w-[3px] transition-opacity duration-150',
          checked ? 'bg-[#035DB8] opacity-100 dark:bg-sky-400' : 'opacity-0',
        ].join(' ')}
      />

      <div className="flex items-start gap-3.5 py-4 pl-5 pr-4 sm:py-[18px] sm:pl-6 sm:pr-5">
        <label htmlFor={checkboxId} className="flex min-w-0 flex-1 cursor-pointer items-start gap-3.5">
          <input
            id={checkboxId}
            type="checkbox"
            checked={checked}
            onChange={onToggle}
            aria-describedby={`${checkboxId}-summary`}
            className="peer sr-only"
          />
          <span
            aria-hidden="true"
            className={[
              'mt-[1px] flex h-[21px] w-[21px] flex-shrink-0 items-center justify-center rounded-[6px] border-2 transition-all duration-150',
              'peer-focus-visible:ring-4 peer-focus-visible:ring-[#035DB8]/25',
              checked
                ? 'border-[#035DB8] bg-[#035DB8] dark:border-sky-400 dark:bg-sky-400'
                : 'border-[#c3d2e0] bg-white dark:border-white/30 dark:bg-transparent',
            ].join(' ')}
          >
            {checked && (
              <svg width="12" height="12" viewBox="0 0 13 13" fill="none">
                <path d="M2.5 6.6L5.4 9.5L10.5 3.6" stroke="white" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </span>

          <span className="min-w-0 flex-1">
            <span className="block text-[14.5px] font-semibold leading-[1.45] tracking-[-0.005em] text-[#17314b] dark:text-slate-100">
              {item.label}
            </span>
            <span
              id={`${checkboxId}-summary`}
              className="mt-1 block max-w-[62ch] text-[13px] leading-[1.6] text-[#5b7186] dark:text-slate-300"
            >
              {item.summary}
            </span>
          </span>
        </label>

        <button
          type="button"
          onClick={() => setExpanded((x) => !x)}
          aria-expanded={expanded}
          aria-controls={detailsId}
          className="mt-px flex flex-shrink-0 items-center gap-1 rounded-md px-2 py-1 text-[12px] font-semibold text-[#035DB8] outline-none transition-colors hover:bg-[#eaf4fd] focus-visible:ring-2 focus-visible:ring-[#035DB8]/30 dark:text-sky-300 dark:hover:bg-sky-400/10"
        >
          {expanded ? 'Hide' : 'Details'}
          <ChevronDown
            size={13}
            className={['transition-transform duration-200', expanded ? 'rotate-180' : ''].join(' ')}
          />
        </button>
      </div>

      {/* Layer 2 — Rule 3 itemised notice */}
      {expanded && (
        <div
          id={detailsId}
          className="border-t border-[#e8eef5] bg-[#fbfdfe] px-5 py-4 sm:px-6 dark:border-white/[0.07] dark:bg-black/20"
        >
          <dl className="grid gap-x-8 gap-y-3.5 sm:grid-cols-2">
            {sections.map((s) => (
              <div key={s.label}>
                <dt className="text-[10.5px] font-bold uppercase tracking-[0.11em] text-[#8398ab] dark:text-slate-400">
                  {s.label}
                </dt>
                <dd className="mt-1 text-[12.5px] leading-[1.6] text-[#4c6379] dark:text-slate-300">
                  {s.list ? (
                    <ul className="space-y-1">
                      {s.values.map((v) => (
                        <li key={v} className="flex gap-2">
                          <span className="mt-[7px] h-1 w-1 flex-shrink-0 rounded-full bg-[#a9bccd]" aria-hidden="true" />
                          <span>{v}</span>
                        </li>
                      ))}
                    </ul>
                  ) : s.values[0]}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
};

/* ── Main ─────────────────────────────────────────────────────────────────── */

const IndiaConsentForm: React.FC<Props> = ({
  surface = 'onboarding',
  initialState,
  onStatusChange,
  onChange,
  triggerSubmit,
  onComplete,
  compact = false,
  contextLabel,
  hideHeading = false,
}) => {
  const items = useMemo(
    () => (surface === 'settings' ? INDIA_CONSENT_ITEMS : getIndiaConsentsForSurface(surface)),
    [surface],
  );

  const [state, setState] = useState<Record<string, boolean>>(
    () => initialState ?? buildInitialConsentState(items),
  );

  const requiredItems = useMemo(() => items.filter((i) => i.required), [items]);
  const acceptedCount = requiredItems.filter((i) => state[i.id]).length;
  const ready = areRequiredConsentsGiven(state, surface);

  useEffect(() => { onStatusChange?.(ready); }, [ready, onStatusChange]);
  useEffect(() => { onChange?.(state); }, [state, onChange]);

  const toggle = useCallback((id: string) => {
    setState((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const submit = useCallback(() => {
    if (!areRequiredConsentsGiven(state, surface)) {
      toast.error(STRINGS.requiredNotAccepted);
      return;
    }
    onComplete?.(state);
  }, [state, surface, onComplete]);

  const submitRef = useRef(submit);
  const registered = useRef(false);
  useEffect(() => { submitRef.current = submit; }, [submit]);
  useEffect(() => {
    if (triggerSubmit && !registered.current) {
      triggerSubmit(() => submitRef.current());
      registered.current = true;
    }
  }, [triggerSubmit]);

  return (
    <div className="w-full">
      {!hideHeading && (
        <div className="mb-4 flex items-start gap-3.5">
          <IconTile accent="brand" size="lg"><ShieldCheck size={22} /></IconTile>
          <div>
            <h2 className="text-[19px] font-bold tracking-tight text-slate-900 dark:text-slate-50">
              Your consent
            </h2>
            <p className="mt-1 text-[13px] text-slate-500 dark:text-slate-400">
              We need your agreement before we can handle your medical records.
            </p>
          </div>
        </div>
      )}

      {/* ── Layer 0 ── */}
      {!compact && (
        <div className="mb-4 flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50/80 p-3.5 dark:border-rose-500/25 dark:bg-rose-500/[0.09]">
          <span className="mt-px flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-rose-500 text-white">
            <AlertCircle size={15} />
          </span>
          <p className="text-[12.5px] font-medium leading-relaxed text-rose-800 dark:text-rose-200">
            {STRINGS.emergencyWarning}
          </p>
        </div>
      )}

      {/* ── Layer 1 ── */}
      <div className="overflow-hidden rounded-[14px] border border-[#dde7f0] bg-white shadow-[0_1px_2px_rgba(16,42,67,.04),0_14px_36px_-28px_rgba(3,93,184,.28)] dark:border-white/[0.09] dark:bg-[#13243a] dark:shadow-none">
        {requiredItems.length > 0 && (
          <div className="border-b border-[#e8eef5] bg-[#f7fafd] px-5 py-4 sm:px-6 dark:border-white/[0.07] dark:bg-white/[0.025]">
            <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
              <p className="text-[13.5px] font-bold tracking-[-0.005em] text-[#193b59] dark:text-slate-100">
                {contextLabel ? `Permissions for your ${contextLabel} review` : 'Permissions for your review'}
              </p>
              <p
                role="status"
                aria-live="polite"
                className={[
                  'text-[12px] font-bold tabular-nums transition-colors',
                  ready ? 'text-emerald-600 dark:text-emerald-400' : 'text-[#6b8299] dark:text-slate-300',
                ].join(' ')}
              >
                {ready ? 'All accepted' : `${acceptedCount} of ${requiredItems.length} accepted`}
              </p>
            </div>
            <p className="mt-1 text-[12.5px] leading-[1.55] text-[#63798f] dark:text-slate-300">
              Each permission is separate — read it and tick it to agree. All of them are needed to prepare this review.
            </p>

            {/* Progress: segment per required item, so "how many left" is legible at a glance */}
            <div className="mt-3 flex gap-1" aria-hidden="true">
              {requiredItems.map((r, i) => (
                <span
                  key={r.id}
                  className={[
                    'h-[3px] flex-1 rounded-full transition-colors duration-300',
                    i < acceptedCount
                      ? 'bg-[#035DB8] dark:bg-sky-400'
                      : 'bg-[#dde7f0] dark:bg-white/[0.12]',
                  ].join(' ')}
                />
              ))}
            </div>
          </div>
        )}

        <div>
          {items.map((item, i) => (
            <ConsentRow
              key={item.id}
              item={item}
              index={i}
              checked={!!state[item.id]}
              onToggle={() => toggle(item.id)}
            />
          ))}
        </div>
      </div>

      {/* ── Layer 3 ── */}
      {!compact && (
        <div className="mt-4 overflow-hidden rounded-[14px] border border-[#dde7f0] bg-white dark:border-white/[0.09] dark:bg-[#13243a]">
          <Disclosure title="About this second opinion" first>
            <div className="space-y-2 text-[12.5px] leading-relaxed text-slate-600 dark:text-slate-300">
              <p>{STRINGS.notPrescriptive}</p>
              {CLINICAL_GOVERNANCE.overseasReviewersInvolved && (
                <p>
                  Specialists outside India may review your case and give an academic opinion.
                  Any clinical advice or prescription you receive is issued and signed by an
                  India-registered medical practitioner
                  {CLINICAL_GOVERNANCE.responsibleRmpRegNo
                    ? ` (Reg. No. ${CLINICAL_GOVERNANCE.responsibleRmpRegNo})`
                    : ''}
                  , who is responsible for it.
                </p>
              )}
              <p className="border-t border-slate-200 pt-2 dark:border-white/[0.08]">
                {STRINGS.pcpndt}
              </p>
            </div>
          </Disclosure>

          <Disclosure title="Your rights">
            <ul className="space-y-1 text-[12.5px] leading-relaxed text-slate-600 dark:text-slate-300">
              <li>• Ask us what data we hold about you and get a copy of it.</li>
              <li>• Have wrong or incomplete information corrected.</li>
              <li>• Ask us to erase your data, except where the law requires us to keep it.</li>
              <li>• Withdraw any consent at any time — as easily as you gave it.</li>
              <li>• Nominate someone to exercise these rights if you are unable to.</li>
              <li>
                • We answer requests within {RETENTION.rightsRequestMaxDays} days, and give you
                copies of your medical records within {RETENTION.recordRequestTurnaroundHours} hours.
              </li>
            </ul>
            <div className="mt-3 space-y-0.5 border-t border-slate-200 pt-2.5 text-[12px] text-slate-500 dark:border-white/[0.08] dark:text-slate-400">
              <p className="font-semibold text-slate-600 dark:text-slate-300">Questions or complaints</p>
              <p>Data Protection Officer: {DP_CONTACTS.dpoName} — {DP_CONTACTS.dpoEmail}</p>
              <p>Grievance Officer: {DP_CONTACTS.grievanceOfficerName} — {DP_CONTACTS.grievanceOfficerEmail}</p>
              <p className="pt-1">
                If we do not resolve your complaint, you can escalate to the Data Protection
                Board of India.
              </p>
            </div>
          </Disclosure>
        </div>
      )}

      {!compact && (
        <p className="mt-3.5 text-center text-xs text-slate-600 dark:text-slate-300">
          You can change any of these later under Consent &amp; Privacy in your profile.
        </p>
      )}
    </div>
  );
};

export default IndiaConsentForm;
